import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import {
    commonFileTypeStoreGetMetadataActivityReadSettings,
    commonFileTypeStoreDeleteActivityReadSettings,
    commonFileTypeStoreWriteSettingControls,
    commonBinaryStoreWriteSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    getCommonBinaryStoreReadSettingControls,
    getCommonDeltaStoreReadSettingControls,
    commonDeltaStoreWriteSettingControls,
    cdcDestination
} from '../consts/metadata-consts';
import { binaryMetadata } from '../dataset/binary-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ConnectionAuthKind } from 'shell-models-lib';
import {
    avro,
    cdm,
    delimited,
    delta,
    excel,
    json,
    orc,
    parquet,
    xml
} from 'app/registries/dataflow/dataflow-file-format-metadata';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { type ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';
import { deltaMetadata } from '../dataset/delta-metadata';

export const googleCloudStorageMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(
        injector,
        'ExposureControlService'
    );
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.googleCloudStorage.linkedServiceType,
        displayName: stringResources.GoogleCloudStorage_AmazonS3API_DisplayText,
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-Google_Cloud_Storage'
        },
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'GoogleCloudStorage' as ResourceKind
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        ...(GlobalVariables.copyJobGenericCdc ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        isFirstClass: true,
        supportDataFlowSource: () => exposureControlService.enableGoogleCloudStorageSourceDataFlow,
        dataflowSource: [avro, cdm, delimited, delta, excel, json, orc, parquet, xml],
        supportGetMetadataActivity: true,
        supportIncrementalBinaryCopy: true,
        supportDatasetV2: true,
        notSupportCopySink: !__IS_FABRIC_DPE__,
        supportTestConnectionToFilePath: true,
        locationPattern: {
            bucketName: '([^/]*)/?[^]*',
            folderPath: '[^/]*/([^]*)/[^/]*',
            fileName: '[^]*/([^/]*)'
        },
        containerLabel: stringResources.InputBucketPlaceHolder,
        containerLabelPlural: stringResources.InputBucketsPlaceHolder,
        locationPropertiesRequireParameters: ['folderPath', 'fileName'],
        mergeReadSettingsInTrident: true,
        getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(true),
        datasetTypeToStoreReadSettingControls: {
            [binaryMetadata.type]: getCommonBinaryStoreReadSettingControls(true),
            [deltaMetadata.type]: getCommonDeltaStoreReadSettingControls()
        },
        storeWriteSettingControls: [...commonFileTypeStoreWriteSettingControls],
        datasetTypeToStoreWriteSettingControls: {
            [binaryMetadata.type]: [...commonBinaryStoreWriteSettingControls],
            [deltaMetadata.type]: [...commonDeltaStoreWriteSettingControls]
        },
        category: ['File'],
        controls: [
            {
                type: 'textbox',
                bind: 'accessKeyId',
                label: stringResources.ModelName_AccessKeyID,
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                isLabelVertical: true
            },
            {
                type: 'dynamic-akv-switcher',
                bind: 'secretAccessKey',
                label: stringResources.ModelName_AmazonAccessKey,
                child: {
                    type: 'password',
                    label: stringResources.ModelName_AmazonAccessKey,
                    required: true,
                    isLabelVertical: true
                },
                validators: [{ type: 'required', errorMessage: '' }],
                description: format(
                    stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AmazonS3_DisplayText),
                    stringResources.ModelName_AmazonAccessKey
                )
            },
            {
                type: 'textbox',
                bind: 'serviceUrl',
                label: stringResources.ModelName_ServiceUrl,
                isLabelVertical: true,
                description: stringResources.AmazonS3_ServiceUrl_Description,
                warningMessageCalculator: (value: string) =>
                    stringIsNullOrEmpty(value) || value === 'https://storage.googleapis.com'
                        ? ''
                        : stringResources.infoSentViaUrlWarning
            }
        ],
        copySinkTypes: [],
        copySourceTypes: []
    };

    return metadata;
};
